import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Zap, Crown } from 'lucide-react';
import * as Icons from 'lucide-react';
import { connectorPlugins } from '../../data/connectorPlugins';
interface ConnectorPluginsPanelProps {
  connectorId: string | null;
  open: boolean;
  onClose: () => void;
}
export function ConnectorPluginsPanel({
  connectorId,
  open,
  onClose
}: ConnectorPluginsPanelProps) {
  const [activePlugins, setActivePlugins] = useState<Set<string>>(new Set());
  useEffect(() => {
    if (open && connectorId) {
      try {
        const stored = localStorage.getItem(`plugins_${connectorId}`);
        if (stored) {
          setActivePlugins(new Set(JSON.parse(stored)));
        }
      } catch (e) {}
    }
  }, [open, connectorId]);
  const togglePlugin = (id: string) => {
    setActivePlugins(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      try {
        localStorage.setItem(`plugins_${connectorId}`, JSON.stringify(Array.from(next)));
      } catch (e) {}
      return next;
    });
  };
  const plugins = connectorPlugins.filter(p => p.connectorId === connectorId);
  const categories = [{
    id: 'automation',
    label: 'Automatización'
  }, {
    id: 'alerts',
    label: 'Alertas'
  }, {
    id: 'insights',
    label: 'Insights'
  }, {
    id: 'sync',
    label: 'Sincronización'
  }];
  return <AnimatePresence data-id="element-3887">
      {open && connectorId && <>
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="fixed inset-0 z-[100] bg-slate-950/80 backdrop-blur-sm" data-id="element-3888" />

          <motion.div initial={{
        x: '100%'
      }} animate={{
        x: 0
      }} exit={{
        x: '100%'
      }} transition={{
        type: 'spring',
        damping: 25,
        stiffness: 200
      }} className="fixed top-0 right-0 bottom-0 w-full max-w-[400px] bg-slate-900 border-l border-slate-800 shadow-2xl z-[101] flex flex-col" data-id="element-3889">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-800 bg-slate-900/80 backdrop-blur-md shrink-0" data-id="element-3890">
              <div className="flex items-center gap-2" data-id="element-3891">
                <Zap size={18} className="text-indigo-400" data-id="element-3892" />
                <h2 className="text-lg font-bold text-slate-100 capitalize" data-id="element-3893">
                  Plugins de {connectorId}
                </h2>
              </div>
              <button onClick={onClose} className="p-2 text-slate-400 hover:text-slate-200 hover:bg-slate-800 rounded-lg transition-colors" title="Cerrar" data-id="element-3894">
                <X size={18} data-id="element-3895" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6 space-y-8 custom-scrollbar" data-id="element-3896">
              {plugins.length === 0 ? <p className="text-sm text-slate-500 text-center py-8" data-id="element-3897">
                  No hay plugins disponibles para este conector.
                </p> : categories.map(cat => {
            const catPlugins = plugins.filter(p => p.category === cat.id);
            if (catPlugins.length === 0) return null;
            return <div key={cat.id} className="space-y-4" data-id="element-3898">
                      <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wider" data-id="element-3899">
                        {cat.label}
                      </h3>
                      <div className="grid grid-cols-1 gap-3" data-id="element-3900">
                        {catPlugins.map(plugin => {
                  const Icon = (Icons as any)[plugin.icon] || Icons.Box;
                  const isActive = activePlugins.has(plugin.id);
                  return <div key={plugin.id} className={`p-4 rounded-xl border transition-all ${isActive ? 'bg-indigo-900/20 border-indigo-500/50' : 'bg-slate-800/40 border-slate-700 hover:border-slate-600'}`} data-id="element-3901">
                              <div className="flex items-start justify-between gap-3" data-id="element-3902">
                                <div className={`p-2 rounded-lg shrink-0 ${isActive ? 'bg-indigo-500/20 text-indigo-400' : 'bg-slate-900 text-slate-400'}`} data-id="element-3903">
                                  <Icon size={18} data-id="element-3904" />
                                </div>
                                <div className="flex-1 min-w-0" data-id="element-3905">
                                  <div className="flex items-center gap-2 mb-1" data-id="element-3906">
                                    <h4 className="text-sm font-bold text-slate-200 truncate" data-id="element-3907">
                                      {plugin.name}
                                    </h4>
                                    {plugin.isPremium && <Crown size={12} className="text-amber-400" data-id="element-3908" />}
                                    {plugin.isNew && <span className="px-1.5 py-0.5 bg-emerald-500/20 text-emerald-400 text-[9px] font-bold rounded-full uppercase tracking-wider" data-id="element-3909">
                                        New
                                      </span>}
                                  </div>
                                  <p className="text-xs text-slate-400 mb-3 line-clamp-2" data-id="element-3910">
                                    {plugin.description}
                                  </p>
                                  <div className="flex items-center justify-between" data-id="element-3911">
                                    <span className="text-[10px] text-slate-500 font-medium" data-id="element-3912">
                                      {plugin.isPremium ? 'Premium' : 'Gratis'}
                                    </span>
                                    <button onClick={() => togglePlugin(plugin.id)} className={`relative w-11 h-6 rounded-full transition-colors ${isActive ? 'bg-indigo-600' : 'bg-slate-700'}`} data-id="element-3913">
                                      <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${isActive ? 'translate-x-5' : 'translate-x-0'}`} data-id="element-3914" />
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </div>;
                })}
                      </div>
                    </div>;
          })}
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
}